import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Card, Typography, Button, Space, Row, Col, Select, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import AntMultiSelect from '../../global/AntMultiSelect';
import { createBrandTitle } from '../../../utils/String';
import { fetchChangeLogItems } from '../../../actions/change_logs';
import {
  FILTER_SEGMENT_NAME,
  FILTER_SEGMENT_TITLE,
  PARENT_PRICE_SHEET_SEGMENT_NAME,
  PARENT_PRICE_SHEET_SEGMENT_TITLE,
  PDM_INTERNAL,
} from '../../../constants/ChangeLogConstants';
import { ApplicationState } from '../../../reducers';
import { AsyncDispatch } from '../../../../types/global';
import { activeBrands } from '../../../selectors/brand/brandSelector';
import { useAppDispatch } from '../../../store/ReduxStore';

const { Text } = Typography;

type ChangeLogSearchCardProps = {
  selectedBrandCodes: string[];
  selectedItemIds: number[];
  selectedSegmentIds: number[];
  selectedLogUserIds: number[];
  handleBrandChange: (value: number[] | undefined) => void;
  handleItemIdsChange: (value: number[] | undefined) => void;
  handleSegmentChange: (value: number[] | undefined) => void;
  handleLogUserIdsChange: (value: number[] | undefined) => void;
  handleFilterSearch: () => void;
};

const ChangeLogSearchCard: React.FC<ChangeLogSearchCardProps> = ({
  selectedBrandCodes,
  selectedItemIds,
  selectedSegmentIds,
  selectedLogUserIds,
  handleBrandChange,
  handleItemIdsChange,
  handleSegmentChange,
  handleLogUserIdsChange,
  handleFilterSearch,
}) => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const [searchKeywords, setSearchKeywords] = useState<string>('');

  const brands = useSelector((state: ApplicationState) => activeBrands(state));
  const changeLogItems = useSelector((state: ApplicationState) => state.changeLogs.items || []);
  const fetchingItems = useSelector((state: ApplicationState) => state.changeLogs.fetchingItems);
  const segments = useSelector(
    (state: ApplicationState) => state.resources.data.global?.segments || []
  );
  const userNameList = useSelector((state: ApplicationState) => state.user.userNameList);

  const activeBrandIds = useMemo(() => brands.map(brand => brand.id), [brands]);

  const selectedBrandIds = useMemo(() => {
    if (selectedBrandCodes.length === 0) {
      return [];
    }
    return selectedBrandCodes
      .map(code => brands.find(brand => brand.code === code)?.id)
      .filter((id): id is number => id !== undefined);
  }, [selectedBrandCodes, brands]);

  const changeLogBrandIds = useMemo(() => {
    return selectedBrandIds.length > 0 ? selectedBrandIds : activeBrandIds;
  }, [selectedBrandIds, activeBrandIds]);

  const handleItemsClick = useCallback(() => {
    if (changeLogItems.length === 0 && !fetchingItems && changeLogBrandIds.length > 0) {
      if (searchKeywords === '' && changeLogBrandIds.length === activeBrandIds.length) {
        dispatch(fetchChangeLogItems(changeLogBrandIds.slice(0, 5)));
      } else {
        dispatch(fetchChangeLogItems(changeLogBrandIds, searchKeywords || undefined));
      }
    }
  }, [
    changeLogItems.length,
    fetchingItems,
    changeLogBrandIds,
    activeBrandIds,
    searchKeywords,
    dispatch,
  ]);

  useEffect(() => {
    if (selectedBrandIds.length > 0) {
      dispatch(fetchChangeLogItems(selectedBrandIds));
    } else {
      dispatch(fetchChangeLogItems(changeLogBrandIds.slice(0, 5)));
    }
  }, [selectedBrandIds, changeLogBrandIds, dispatch]);

  const availableSegments = useMemo(() => {
    let changeLogSegments = segments;
    changeLogSegments = changeLogSegments.filter(
      segment => segment.name !== 'flat_applications' && segment.name !== 'equipment'
    );
    const additionalAvailableSegments = [
      { id: 21, name: FILTER_SEGMENT_NAME, title: FILTER_SEGMENT_TITLE },
      { id: 22, name: PARENT_PRICE_SHEET_SEGMENT_NAME, title: PARENT_PRICE_SHEET_SEGMENT_TITLE },
    ];
    changeLogSegments = [...changeLogSegments, ...additionalAvailableSegments];
    return changeLogSegments;
  }, [segments]);

  const segmentElements = useMemo(
    () =>
      availableSegments.map(segment => ({
        id: segment.id,
        name: segment.title ? segment.title : '',
      })),
    [availableSegments]
  );

  const brandElements = useMemo(
    () =>
      brands.map(brand => ({
        id: brand.id,
        value: brand.id,
        name: `${brand.name} (${brand.code})`,
        code: brand.code,
      })),
    [brands]
  );

  const itemElements = useMemo(
    () =>
      changeLogItems.map(item => {
        const brandTitle = createBrandTitle(
          item.brand_name || item.brand_code,
          item.brand_code,
          item.default_whitelabel_brand_code || ''
        );
        return {
          id: item.id,
          value: item.id,
          name: `${item.part_number} - ${brandTitle}`,
        };
      }),
    [changeLogItems]
  );

  const userElements = useMemo(() => {
    const userMap = new Map<string, number[]>();

    userNameList.forEach(user => {
      const optionName = user.email?.endsWith('pdm.ai')
        ? PDM_INTERNAL
        : `${user.first_name} ${user.last_name} - ${user.email}`;

      if (userMap.has(optionName)) {
        userMap.get(optionName)!.push(user.id);
      } else {
        userMap.set(optionName, [user.id]);
      }
    });

    return Array.from(userMap.entries()).map(([name, ids]) => ({
      id: ids[0],
      value: ids[0],
      name,
      userIds: ids,
    }));
  }, [userNameList]);

  const handleItemSearch = useCallback(
    (value: string) => {
      setSearchKeywords(value);
      if (value && changeLogBrandIds.length > 0) {
        dispatch(fetchChangeLogItems(changeLogBrandIds, value));
      }
    },
    [changeLogBrandIds, dispatch]
  );

  const handleUserChange = useCallback(
    (selectedValues: number[] | undefined) => {
      if (!selectedValues) {
        handleLogUserIdsChange(undefined);
        return;
      }

      const allUserIds = selectedValues.flatMap(selectedValue => {
        const userElement = userElements.find(element => element.value === selectedValue);
        return userElement?.userIds || [selectedValue];
      });

      const uniqueUserIds = Array.from(new Set(allUserIds));
      handleLogUserIdsChange(uniqueUserIds);
    },
    [userElements, handleLogUserIdsChange]
  );

  const selectedUserOptionValues = useMemo(() => {
    if (!selectedLogUserIds || selectedLogUserIds.length === 0) {
      return [];
    }

    const selectedOptionIds = new Set<number>();
    selectedLogUserIds.forEach(userId => {
      const userElement = userElements.find(element => element.userIds.includes(userId));
      if (userElement) {
        selectedOptionIds.add(userElement.value);
      }
    });

    return Array.from(selectedOptionIds);
  }, [selectedLogUserIds, userElements]);

  return (
    <Card className="change-logs-container__filter-card">
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Space direction="vertical" className="change-logs-filter__space">
            <Text strong>{t('changeLog:brands')}</Text>
            <AntMultiSelect
              elements={brandElements}
              selectedElements={selectedBrandCodes
                .map(code => brandElements.find(brand => brand.code === code)?.id)
                .filter((id: number | undefined) => id !== undefined)}
              onChange={handleBrandChange}
              placeholder={t('changeLog:brandSelectPlaceholder')}
              className="change-logs-filter__select"
              allowClear
              showSearch
            />
          </Space>
        </Col>
        <Col span={12}>
          <Space direction="vertical" className="change-logs-filter__space">
            <Text strong>{t('changeLog:items')}</Text>
            <Select
              mode="multiple"
              value={selectedItemIds}
              onChange={handleItemIdsChange}
              onFocus={handleItemsClick}
              onSearch={handleItemSearch}
              placeholder={t('changeLog:itemSelectPlaceholder')}
              className="change-logs-filter__select"
              allowClear
              showSearch
              optionFilterProp="label"
              getPopupContainer={trigger => (trigger && trigger.parentNode) || document.body}
              options={itemElements.map(element => ({
                key: element.id,
                value: element.id,
                label: element.name,
              }))}
              notFoundContent={fetchingItems ? <Spin /> : undefined}
            />
          </Space>
        </Col>
      </Row>
      <Row gutter={[16, 16]} className="change-logs-container__filter-row">
        <Col span={12}>
          <Text strong>{t('changeLog:segments')}</Text>
          <AntMultiSelect
            placeholder={t('changeLog:segementSelectPlaceHolder')}
            elements={segmentElements}
            selectedElements={selectedSegmentIds}
            onChange={handleSegmentChange}
            className="change-logs-filter__select"
            allowClear
            showSearch
          />
        </Col>
        <Col span={12}>
          <Text strong>{t('changeLog:users')}</Text>
          <AntMultiSelect
            placeholder={t('changeLog:logUserSelectPlaceHolder')}
            elements={userElements}
            selectedElements={selectedUserOptionValues}
            onChange={handleUserChange}
            className="change-logs-filter__select"
            allowClear
            showSearch
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]} className="change-logs-container__filter-row">
        <Col span={24} style={{ textAlign: 'right' }}>
          <Space>
            <Button type="primary" onClick={handleFilterSearch}>
              {t('changeLog:search')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleFilterSearch}>
              {t('changeLog:refresh')}
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

export default ChangeLogSearchCard;
